<template>
  <Card
    class="info-card"
    :class="{
      'is-expanded': isExpanded,
      'is-scrollable': scrollable,
      'is-fully-expanded': isFullyExpanded
    }"
    :style="cardStyle"
  >
    <CardSection
      ref="label-ref"
      class="label"
    >
      <Button
        size="sm"
        variant="text"
        :label="label"
        :start-icon="icon"
        :end-icon="expandable ? 'mono-dropdown-arrow' : null"
        :alert="alert"
        :aria-expanded="expandable ? String(isExpanded) : null"
        :aria-controls="expandable ? bodyId : null"
        @click="onToggle"
      />
    </CardSection>
    <Body
      :id="bodyId"
      :variants="bodyInnerVariants"
      initial="expanded"
      :animate="isExpanded ? 'expanded' : 'collapsed'"
      class="body"
      :style="bodyStyle"
      @animation-start="onBodyAnimationStart"
      @animation-complete="onBodyAnimationComplete"
    >
      <div class="inner">
        <Separator color="float" />

        <q-scroll-area v-if="scrollable">
          <div class="content">
            <slot />
          </div>
        </q-scroll-area>

        <slot v-else />

        <template v-if="$slots.footer">
          <Separator color="float" />

          <div class="footer">
            <div class="footer-actions">
              <slot name="footer" />
            </div>
          </div>
        </template>
      </div>
    </Body>
  </Card>
</template>

<script setup>
import { ref, computed, watch, useTemplateRef } from 'vue'
import { motion } from 'motion-v'
import CardSection from '@/components/ui/Card/CardSection.vue'

defineOptions({ name: 'InfoCard' })
const isExpanded = defineModel({ type: Boolean, default: true })
const bodyId = `info-card-body-${Math.random().toString(36).slice(2)}`
const props = defineProps({
  icon: { type: String, default: null },
  label: { type: String, default: null },
  expandable: { type: Boolean, default: true },
  scrollable: { type: Boolean, default: false },
  contentHeight: { type: Number, default: null },
  alert: {
    type: String,
    default: null,
    validator: v => [
      'primary',
      'secondary',
      'positive',
      'negative',
      'warning',
      'info',
      'accent',
      'invert'
    ].includes(v)
  }
})

const Body = motion.create(CardSection)

const labelRef = useTemplateRef('label-ref') // eslint-disable-line
const contentRef = useTemplateRef('content-ref') // eslint-disable-line

const bodyStyle = ref({ flex: props.scrollable ? '0 0 auto' : '1 1 0' })

const isAnimating = ref(false)
const isFullyExpanded = ref(isExpanded.value)
const cardStyle = computed(() => {
  if (!labelRef.value?.$el) return {}
  const maxHeight = props.scrollable
    ? props.contentHeight
      ? `${props.contentHeight + labelRef.value.$el.getBoundingClientRect().height + 26}px`
      : null
    : null
  return { maxHeight }
})
const bodyInnerVariants = computed(() => ({
  expanded: {
    opacity: 1,
    visibility: 'visible',
    height: props.scrollable ? props.contentHeight + 24 : 'auto',
    transition: { duration: 0.3 }
  },
  collapsed: {
    opacity: 0,
    visibility: 'hidden',
    height: 0,
    transition: { duration: 0.3 }
  }
}))

const onToggle = () => {
  isExpanded.value = !isExpanded.value
}

const onBodyAnimationStart = latest => {
  isAnimating.value = true
  bodyStyle.value.flex = '0 0 auto'
}
const onBodyAnimationComplete = latest => {
  if (latest === 'expanded') isFullyExpanded.value = true
  bodyStyle.value.flex = '1 1 0'
}

watch(isExpanded, val => {
  if (!val) isFullyExpanded.value = false
})
watch(() => props.contentHeight, val => {
})

</script>

<style scoped>
@reference "#app-styles.css";

.info-card {
  @apply flex flex-col min-h-0;

  .label {
    @apply shrink flex flex-nowrap gap-1 text-[12px] leading-3.5 font-semibold;
    .q-icon { @apply text-[14px] leading-3.5; }
    &:deep(.button-standard) .on-right { @apply transition-transform duration-300 ease-out -rotate-90; }
  }
  .body {
    @apply flex flex-col p-0! overflow-y-clip;

    &:deep(.q-scrollarea) { @apply min-h-full; }

    /* Zero padding on both axes — every piece of actual content (the default
       slot's own containers, the footer slot) owns its own padding instead, so
       full-bleed elements (the separators below, the footer frame) span the
       card's full width with no negative-margin bleed hack needed. */
    .inner { @apply flex flex-col h-full py-0; }
  }

  /* Mirrors brand search's ViewMenu footer exactly: an outer 44px-tall frame
     (8px vertical padding) wrapping an inner flex row (2px vertical / 16px
     horizontal padding, 2px gap) that holds the footer slot's buttons. */
  .footer {
    @apply h-11 py-2;

    .footer-actions {
      @apply flex items-center justify-end gap-[2px] h-full px-lg py-[2px];
    }
  }

  &.is-scrollable.is-fully-expanded { @apply h-full; }
  &.is-expanded {
    .label :deep(.button-standard) .on-right { @apply rotate-0; }
  }
}
</style>
